// React
import {useState, useEffect} from "react";

// OpenLayers
import OlMap from "ol/Map";
import { transform } from "ol/proj";

// Models
import { Popup, PopupProps } from "lib/classes/popup";
import Address from "lib/classes/address";
import MapToolMode from "lib/enums/maptoolmode";

// Lang
import { getLocaleString } from "lib/localization/vendor";

// UI
import AddressesPopup from "views/popups/Addresses";

// Tools
import { fetchRequestJson } from "lib/utilities/network";
import { makeUrl } from "lib/utilities/misc";

// Styles
import "styles/css/AddressTool.scss";

var page:number = 0;
var totalPages:number = 0;

const AddressSearchPopup = new Popup({
	title: () => getLocaleString("addresssearch"),
	render: function() {
		// State hooks
		const [loading, setLoading] = useState<boolean>(false);
		const [addressPath, setAddressPath] = useState<string>(this.get("address-path") || "");
		const [postcode, setPostcode] = useState<string>(this.get("post-code") || "");
		
		// Map and map config
		const map:OlMap = this.get("map");
		const mapConfig:any = map.get("opus-config");
		
		/**
			* Show address popup
			*
			* @param addresses	-	Addresses array
		*/
		const showAddressesPopup = (addresses:Address[]) => {
			AddressesPopup.set("addresses", addresses);
			AddressesPopup.set("selected", addressSelected);
			AddressesPopup.set("next", nextAddressesPage);
			AddressesPopup.set("prev", prevAddressesPage);
			AddressesPopup.set("page", page);
			AddressesPopup.set("total", totalPages);
			
			_ui.showPopup(AddressesPopup);
		}
		
		/**
			* Address selection handler
			*
			* @param address	-	Address selected
			*
			* @remarks
			* Passed to the addresses popup as a Popup property
			* and called from there.
		*/
		const addressSelected = (address:Address) => {
			const coords = transform(
				[address.x, address.y],
				"EPSG:27700",
				"EPSG:3857"
			);
			
			map.getView().setCenter(coords);
			map.getView().setZoom(19);
			
			// Drop pin by default
			_events.invoke('place-marker', coords, [_dimensions.width / 2, _dimensions.height / 2]);
			
			// Go back to select tool
			_ui.setToolMode(MapToolMode.Select);
			
			_ui.closePopupOf(AddressesPopup);
			_ui.closePopup();
		}
		
		/** 
			* Submit address search
			* 
			* @param e	-	On click event
			* 
			* @remarks
			* Called by button onClick
		*/
		const submitAddress = (e:any) => {
			if(!addressPath && !postcode) 
				return _ui.showError(getLocaleString("addrinputerror"));
			
			setLoading(true);
			
			// Reset current page
			page = 1;
			
			searchAddress();
		}
		
		/**
			Search addresses API call method
		*/
		const searchAddress = () => {
			_ui.showLoading();
			
			const url:string = makeUrl(mapConfig.addressSearch.url, new Map<string, any>([
				["address", addressPath],
				["postcode", postcode],
				["page", page],
				["size", 25],
				["client", mapConfig.client],
			]))
			
			const options = {
				method: "GET",
				headers: new Headers(),
				mode: "cors",
				cache: "default"
			};
			
			fetchRequestJson(url, options)
			.then((data:any) => {
				setLoading(false);
				_ui.hideLoading();
				
				const addresses:Address[] = [];
				
				if(data.Message) return _ui.showError(data.Message);
				if(!data.Results?.Addresses?.Address) return _ui.showError(getLocaleString("noaddr"));
				
				const addressesData = data.Results.Addresses.Address;
				const metadata = data.Results.MetaData;
				const results = Array.isArray(addressesData) ? addressesData : [addressesData];
				
				totalPages = metadata.TotalPages;
				
				for(let result of results) {
					addresses.push(
						new Address({
							id: result.ID, 
							fullAddress: result.FullAddress, 
							postcode: result.Postcode,
							x: Number(result.X),
							y: Number(result.Y)
						})
					);
				}
				
				_ui.closePopupOf(AddressesPopup);
				showAddressesPopup(addresses);
			})
			.catch((error) => {
				setLoading(false);
				_ui.hideLoading();
				
				_ui.showError(`There was an error with the request. ${error}`);
			});
		}
		
		/*
			Go to next address page
		*/
		const nextAddressesPage = () => {
			let newPage:number = page + 1;
			
			if(newPage > totalPages) newPage = totalPages;
			
			page = newPage;
			
			searchAddress();
		}
		
		/*
			Go to previous address page
		*/
		const prevAddressesPage = () => {
			let newPage:number = page - 1;
			
			if(newPage < 1) newPage = 1;
			
			page = newPage;
			
			searchAddress();
		}
		
		/*
			Input handling methods
		*/
		const onAddressPathChange = (e:any) => {
			setAddressPath(e.target.value);
			this.set("address-path", e.target.value || "");
		}
		const onPostcodeChange = (e:any) => {
			setPostcode(e.target.value);
			this.set("post-code", e.target.value || "");
		}
		const clearAddress = () => {
			setAddressPath("");
			setPostcode("");
			
			this.set("address-path", "");
			this.set("post-code", "");
		}
		
		return (
			<>
				<div className="address-form">
					<div className="address-form__inputs">
						<input 
							type="search" 
							placeholder={getLocaleString("address")}
							value={addressPath || ""} 
							onChange={onAddressPathChange} 
						/>
						<input 
							type="search" 
							placeholder={getLocaleString("postcode")}
							value={postcode || ""} 
							onChange={onPostcodeChange} 
							className="postcode-input"
						/>
					</div>
					
					<button onClick={submitAddress} disabled={loading}>
						{loading ? `${getLocaleString("searching")}...` : getLocaleString("search")}
					</button> 
					<button onClick={clearAddress} disabled={loading} style={{marginLeft: 5}}>
						{getLocaleString("clear")}
					</button>
				</div>
			</>
		)
	}
});

export default AddressSearchPopup;